import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { LOGO, DISCORD_URL } from "@/components/StarsBackground";
import { Menu, X, Shield, LogIn, LogOut } from "lucide-react";
import { useAuth, loginWithDiscord } from "@/hooks/useAuth";

const links = [
  { label: "Pricing", href: "/#pricing" },
  { label: "Clips", href: "/#clips" },
  { label: "FAQ", href: "/#faq" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const { me, logout } = useAuth();

  useEffect(() => {
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const avatarUrl = me?.avatar
    ? `https://cdn.discordapp.com/avatars/${me.discord_id}/${me.avatar}.png?size=64`
    : null;

  return (
    <header className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      <div className="w-full px-4 md:px-6 h-20 flex items-center justify-between gap-4 relative">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <img src={LOGO} alt="Rumple's Values" className="h-12 w-12 rounded-full ring-1 ring-white/10" />
          <div className="hidden sm:block">
            <div className="font-display font-black uppercase tracking-tight text-lg leading-none">
              Rumple<span style={{ color: "var(--rv-red)" }}>{"'s"}</span> Values
            </div>
            <div className="text-[10px] tracking-[0.3em] text-white/50 uppercase mt-1">Cronus Zen · Apex Legends</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-${l.label.toLowerCase()}`} className="text-sm font-semibold uppercase tracking-widest text-white/70 hover:text-white transition">
              {l.label}
            </a>
          ))}
          {me && (
            <Link to="/values" className="text-sm font-semibold uppercase tracking-widest text-white/70 hover:text-white transition" data-testid="nav-values">
              My Values
            </Link>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {me ? (
            <div
              ref={userMenuRef}
              className="relative"
              data-testid="header-user"
            >
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 rounded-full border border-white/12 bg-white/[0.04] hover:border-white/30 transition"
                data-testid="user-menu-trigger"
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-[var(--rv-red)]/30 flex items-center justify-center text-xs font-bold">
                    {(me.global_name || me.username || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <span className="text-sm font-semibold text-white max-w-[140px] truncate uppercase tracking-wider">
                  {me.global_name || me.username}
                </span>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 min-w-[180px] rounded-md border border-white/10 bg-[#0a0a0a] shadow-xl shadow-black/50 py-1.5 z-50"
                  data-testid="user-menu-dropdown"
                >
                  <Link
                    to="/values"
                    onClick={() => setMenuOpen(false)}
                    className="block px-3 py-2 text-xs uppercase tracking-widest text-white/80 hover:bg-white/5"
                    data-testid="user-menu-values"
                  >
                    My Values
                  </Link>
                  {me.is_admin && (
                    <Link
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                      className="block px-3 py-2 text-xs uppercase tracking-widest text-white/80 hover:bg-white/5"
                      data-testid="user-menu-admin"
                    >
                      Admin panel
                    </Link>
                  )}
                  <div className="h-px bg-white/10 my-1" />
                  <button
                    onClick={() => { setMenuOpen(false); logout(); }}
                    className="w-full text-left px-3 py-2 text-xs uppercase tracking-widest text-white/80 hover:bg-[var(--rv-red)]/20 hover:text-white inline-flex items-center gap-2"
                    data-testid="user-menu-logout"
                  >
                    <LogOut size={12} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={loginWithDiscord} className="btn-outline" data-testid="header-login-btn">
              <LogIn size={12} /> Sign in
            </button>
          )}
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-sm" data-testid="header-discord-btn">
            Join Discord
          </a>
        </div>

        <button className="md:hidden text-white p-2" onClick={() => setOpen((o) => !o)} aria-label="menu" data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#050505]/95 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-sm font-semibold uppercase tracking-widest text-white/80" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
              {l.label}
            </a>
          ))}
          {me && (
            <Link to="/values" onClick={() => setOpen(false)} className="text-sm font-semibold uppercase tracking-widest text-white/80" data-testid="mobile-nav-values">
              My Values
            </Link>
          )}
          {me?.is_admin && (
            <Link to="/admin" onClick={() => setOpen(false)} className="btn-ghost justify-center" data-testid="mobile-admin-btn">
              <Shield size={14} /> Admin
            </Link>
          )}
          {me ? (
            <button onClick={logout} className="btn-ghost justify-center" data-testid="mobile-logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          ) : (
            <button onClick={loginWithDiscord} className="btn-primary justify-center" data-testid="mobile-login-btn">
              <LogIn size={14} /> Sign in / Register
            </button>
          )}
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="btn-primary justify-center mt-2" data-testid="mobile-discord-btn">
            Join Discord
          </a>
        </div>
      )}
    </header>
  );
}
